import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  matVisibility,
  matVisibilityOff,
} from '@ng-icons/material-icons/baseline';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { GoogleRecapchaComponent } from '@components/auth/google-recapcha/google-recapcha.component';
import { AuthenService } from '@core/authen/authen.service';
import { ProfileEmailService } from '@core/authen/profile-email.service';
import { EmailRegexService } from '@core/form/email-regex.service';
import {
  FormComponentInterface,
  FormErrorService,
} from '@core/form/form-error.service';
import { SessionService } from '@core/session.service';
import { ToastService } from '@core/toast.service';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { InfoNoticeComponent } from '@shared/components/atoms/info-notice/info-notice.component';
import { TermAgreementCheckboxComponent } from '@shared/components/atoms/term-agreement-checkbox/term-agreement-checkbox.component';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { LanguagePathDirective } from '@shared/directives/language-path.directive';
import { SessionKeyEnum } from '@shared/enums/session.key.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { ApiResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import {
  EmailActivationStatus,
  RegisterResponse,
} from '@shared/models/authen.model';
import {
  EmailWrapperPipe,
  LinkWrapperPipe,
} from '@shared/pipes/link-wrapper.pipe';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { TextValidator } from '@shared/utils/form-fields-validation';
import { StringUtils } from '@shared/utils/string-utils';
import { CookieService } from 'ngx-cookie-service';
import { PasswordStrengthComponent } from './password-strength.component';

@UntilDestroy()
@Component({
  selector: 'app-form-register',
  templateUrl: './form-register.component.html',
  imports: [
    NgIcon,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    LanguagePathDirective,
    GoogleRecapchaComponent,
    TermAgreementCheckboxComponent,
    InfoNoticeComponent,
    DebounceClickDirective,
    StringReplacementPipe,
    LinkWrapperPipe,
    EmailWrapperPipe,
    SafeHtmlPipe,
    PasswordStrengthComponent,
  ],
  viewProviders: [provideIcons({ matVisibility, matVisibilityOff })],
})
export class FormRegisterComponent implements OnInit, FormComponentInterface {
  @Input() invitationTokens = '';

  @Input() portalName: string = environment.portalName;

  @Input() portalUrl: string = environment.portalUrl;

  @Input() termOfUseUrl = environment.termOfUseUrl;

  @Input() assignToOrgId: number;

  @Input() eventId;

  @Output() sendConfirmationEmail = new EventEmitter();
  @Output() loginSuccess = new EventEmitter();

  usernameMinLength = environment.loginFormLimit.usernameMinLength;
  passwordMinLength = environment.loginFormLimit.passwordMinLength;

  loginForm: UntypedFormGroup;
  formErrorKey: string;
  errorMessage: Record<string, string>;
  isSubmitting?: boolean;
  isSubmitted?: boolean;

  passwordDisplayed = false;
  passwordRepeatIsShow = false;

  isRegisteredAccount = false;
  sentEmailAddress: string;
  accountNotActivated = false;
  notActivatedEmail: string;
  isResending = false;

  showPasswordStrength = false;

  constructor(
    readonly sessionService: SessionService,
    protected readonly toastService: ToastService,
    private readonly fb: FormBuilder,
    readonly authenService: AuthenService,
    private readonly formErrorService: FormErrorService,
    private readonly profileEmailService: ProfileEmailService,
    private readonly cookieService: CookieService,
    private readonly emailRegexService: EmailRegexService,
    private readonly el: ElementRef
  ) {
    this.portalName = this.cookieService.get(SessionKeyEnum.TENANT_NAME);
    this.portalUrl = this.cookieService.get(SessionKeyEnum.TENANT_DOMAIN);
  }

  getForm(): UntypedFormGroup {
    return this.loginForm;
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group(
      {
        firstName: [
          '',
          {
            validators: [
              Validators.required,
              TextValidator.notEmptyOrWhitespace,
            ],
          } as AbstractControlOptions,
        ],
        lastName: [
          '',
          {
            validators: [
              Validators.required,
              TextValidator.notEmptyOrWhitespace,
            ],
          } as AbstractControlOptions,
        ],
        username: [
          '',
          {
            validators: [
              Validators.required,
              TextValidator.notEmptyOrWhitespace,
              TextValidator.cannotContainWhitespace,
            ],
          } as AbstractControlOptions,
        ],
        email: [
          '',
          {
            validators: [
              Validators.required,
              this.emailRegexService.emailValidator(),
            ],
          } as AbstractControlOptions,
        ],
        password: [
          '',
          {
            validators: [
              Validators.required,
              TextValidator.notEmptyOrWhitespace,
            ],
          } as AbstractControlOptions,
        ],
        passwordRepeat: [
          '',
          {
            validators: [Validators.required],
          } as AbstractControlOptions,
        ],
        recaptcha: ['', Validators.required],
        termAgreement: [false, [Validators.requiredTrue]],
      },
      {
        validators: [checkPasswordMatch],
      }
    );
    this.formErrorService.register(this);

    this.loginForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.accountNotActivated = false;
      this.notActivatedEmail = null;
    });
  }

  onSubmit(): void {
    this.isSubmitting = true;
    this.accountNotActivated = false;

    const checkedEmail = this.email.value?.trim();

    this.authenService
      .checkEmailActivation(checkedEmail)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (status: EmailActivationStatus) => {
          // The address may have been edited while the check was in flight;
          // its answer no longer describes what the user is submitting.
          if (checkedEmail !== this.email.value?.trim()) {
            this.isSubmitting = false;
            return;
          }

          if (status?.isExisted && !status.isActivated) {
            this.notActivatedEmail = checkedEmail;
            this.accountNotActivated = true;
            this.isSubmitting = false;
            return;
          }

          this.registerAccount();
        },
        // Fail open: a hiccup in the activation check must never block registration.
        error: () => {
          this.registerAccount();
        },
      });
  }

  checkError(data): void {
    this.formErrorService.handleError(data);
    this.isSubmitting = false;
    this.isSubmitted = false;

    setTimeout(() => this.resendActivationMail());
  }

  togglePassword(event): void {
    event.preventDefault();
    this.passwordDisplayed = !this.passwordDisplayed;
  }

  togglePasswordRepeat(event): void {
    event.preventDefault();
    this.passwordRepeatIsShow = !this.passwordRepeatIsShow;
  }

  get firstName(): AbstractControl<string> {
    return this.loginForm.get('firstName');
  }

  get lastName(): AbstractControl<string> {
    return this.loginForm.get('lastName');
  }

  get username(): AbstractControl<string> {
    return this.loginForm.get('username');
  }

  get email(): AbstractControl<string> {
    return this.loginForm.get('email');
  }
  get password(): AbstractControl<string> {
    return this.loginForm.get('password');
  }

  get passwordRepeat(): AbstractControl<string> {
    return this.loginForm.get('passwordRepeat');
  }

  resendConfirmationEmail(usernameOrEmail: string): void {
    this.isSubmitting = true;
    this.isSubmitted = false;

    const sentToEmail = usernameOrEmail?.trim();
    this.authenService.resendConfirmationEmail(sentToEmail).subscribe({
      next: (data: RegisterResponse | ApiResponse) => {
        const error = data as ApiResponse;
        if (error?.status === 400) {
          this.checkError(error);
        } else {
          const resData = data as RegisterResponse;
          this.completeRegistration(resData);
        }
        this.isSubmitting = false;
      },
      error: error => {
        this.checkError(error);
      },
    });
  }

  /**
   * Resend for an account that exists but is not activated yet, triggered from
   * the error alert on the register form. Unlike resendConfirmationEmail(), it
   * keeps the user on the form and only reports success with a toast.
   */
  resendNotActivatedEmail(): void {
    this.isResending = true;

    this.authenService
      .resendConfirmationEmail(this.notActivatedEmail)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: RegisterResponse | ApiResponse) => {
          const error = data as ApiResponse;
          if (error?.status === 400) {
            this.handleResendError(error);
            return;
          }

          this.isResending = false;
          this.toastService.showSuccess('UI.Toast.SentSuccessfully');
        },
        error: error => {
          this.handleResendError(error);
        },
      });
  }

  /**
   * Add event listener for "click here" link to resend confirmation email
   */
  private resendActivationMail(): void {
    const element = document.getElementById('resendEmail') as HTMLAnchorElement;

    if (!element) return;

    element.onclick = (e: MouseEvent) => {
      e.preventDefault();

      const email = StringUtils.getParamFromUrl(
        UrlParam.ResendEmail,
        element.href
      );
      const token = StringUtils.getParamFromUrl(UrlParam.Token, element.href);

      this.profileEmailService
        .resendChangeEmailAnonymously(email, token)
        .pipe(untilDestroyed(this))
        .subscribe((data: unknown) => {
          if (data['isEmailDelivered']) {
            this.toastService.showSuccess('UI.Toast.SentSuccessfully');
          }
        });
    };
  }

  private scrollToTop(): void {
    this.el.nativeElement.ownerDocument.body.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  /**
   * The not-activated alert owns its own error reporting: checkError() would
   * additionally rewire the legacy "#resendEmail" anchor, which this alert
   * does not render.
   */
  private handleResendError(data: ApiResponse): void {
    this.formErrorService.handleError(data);
    this.isResending = false;
  }

  private registerAccount(): void {
    this.authenService
      .register(this.loginForm.value, this.invitationTokens, this.assignToOrgId)
      .subscribe({
        next: (data: RegisterResponse | ApiResponse) => {
          const error = data as ApiResponse;
          if (error?.status === 400) {
            this.checkError(error);
          } else {
            const resData = data as RegisterResponse;
            this.completeRegistration(resData);
            this.scrollToTop();
          }
        },
        error: error => {
          this.checkError(error);
        },
      });
  }

  private completeRegistration(response: RegisterResponse): void {
    this.isRegisteredAccount = true;
    this.sentEmailAddress = response.email;
    this.sendConfirmationEmail.emit();
    this.isSubmitting = false;
    this.isSubmitted = true;
  }

  onPasswordFocus(): void {
    this.showPasswordStrength = true;
  }

  onPasswordBlur(): void {
    this.showPasswordStrength = false;
  }
}

export const checkPasswordMatch: ValidatorFn = (
  group: UntypedFormGroup
): ValidationErrors | null => {
  const pass = group.get('password').value;
  const confirmPass = group.get('passwordRepeat').value;

  return pass === confirmPass ? null : { passwordNotSame: true };
};
